export interface DiffSegment {
  type: "added" | "removed" | "unchanged";
  text: string;
}

// Simple word-level diff using a greedy approach
export function computeWordDiff(original: string, rewritten: string): DiffSegment[] {
  const origWords = original.match(/\S+|\s+/g) || [];
  const rewriteWords = rewritten.match(/\S+|\s+/g) || [];

  // Use a simple longest common subsequence-based approach
  const lcs = longestCommonSubsequence(origWords, rewriteWords);
  const result: DiffSegment[] = [];

  let origIdx = 0;
  let rewriteIdx = 0;

  for (const word of lcs) {
    // Add removed words before this LCS word
    while (origIdx < origWords.length && origWords[origIdx] !== word) {
      result.push({ type: "removed", text: origWords[origIdx] });
      origIdx++;
    }

    // Add added words before this LCS word
    while (rewriteIdx < rewriteWords.length && rewriteWords[rewriteIdx] !== word) {
      result.push({ type: "added", text: rewriteWords[rewriteIdx] });
      rewriteIdx++;
    }

    // Add unchanged word
    if (origIdx < origWords.length && origWords[origIdx] === word) {
      result.push({ type: "unchanged", text: word });
      origIdx++;
      rewriteIdx++;
    }
  }

  // Add remaining removed words
  while (origIdx < origWords.length) {
    result.push({ type: "removed", text: origWords[origIdx] });
    origIdx++;
  }

  // Add remaining added words
  while (rewriteIdx < rewriteWords.length) {
    result.push({ type: "added", text: rewriteWords[rewriteIdx] });
    rewriteIdx++;
  }

  return result;
}

function longestCommonSubsequence(a: string[], b: string[]): string[] {
  const dp: number[][] = Array(a.length + 1)
    .fill(null)
    .map(() => Array(b.length + 1).fill(0));

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Reconstruct LCS
  const lcs: string[] = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}
